// Ported from nenda-autoplayer's src/components/Playback/Video/ShakaPlayer.tsx.
// Already plain `<video>`/`<div>` upstream — zero React Native imports there, so this is
// a near-verbatim port. Two deltas from upstream, both called out inline below:
//
//   1. FIX (confirmed by direct API-surface check against Foyer's installed shaka-player
//      v5.2.4, which autoplayer's own package.json pins to ^4.13.6): `setTextTrackVisibility`
//      was renamed to `setTextVisibility` in Shaka v5. Everything else this file touches on
//      the DRM-critical path (attach/destroy/configure/getKeyStatuses/drmInfo/
//      getNetworkingEngine/request-response filters/Error.Code enums/RequestType.LICENSE)
//      checked out compatible — see the plan file for the full check.
//   2. ADDITIVE (per the port's target architecture, §4/§5): two optional callback props,
//      `onDrmNegotiated` and `onDrmCategory6Failure`, surfacing state this component already
//      computes internally but didn't previously expose. Foyer's glue layer
//      (SignageEngineSurface) needs the negotiated keySystem for real telemetry (fixing the
//      "reports playing while black" bug from the 2026-09-17/18 incident) and the category-6
//      DRM failure signal to drive its native-ExoPlayer fallback via FoyerBridge — without
//      either callback wired up, this component behaves exactly as upstream.
//
// THE KEY PROPERTY THIS FILE PROVIDES (why this port exists): a single Shaka instance per
// mount, gated behind the async dynamic import, destroyed on unmount; the caller is expected
// to force a full remount on every asset change (see VideoPlayer.tsx's `key={id-playId}`
// pattern) rather than reusing one long-lived instance across loads — that discipline, plus
// the 30s startup watchdog below, is the structural fix for the double-DRM-configure race that
// left a Philips panel stuck at readyState=0 for hours on 2026-09-17/18.
// shaka-player's own .d.ts ends in `export default shaka;`, making the whole file an ES
// module — so its `declare namespace shaka {...}` is module-local, not a global ambient
// (unlike what upstream autoplayer's bare `shaka.*` type references assume; that repo's
// Expo/Metro toolchain apparently resolves it differently, or tolerates the gap since it
// ships with zero tests). A type-only import gives the exact same namespace as a proper,
// locally-scoped type — `shaka` here is a TYPE only, never a value; the dynamically loaded
// runtime module is the separate `Shaka` (capital S) state variable below, same as upstream.
import type shaka from "shaka-player";
import { PlaybackErrorLevel } from "../types/error";
import { reportSignageMediaStall } from "../../lib/signagePlayback";
import { DrmConfig } from "../types/DrmConfig";
import React, { memo, useCallback, useEffect, useRef } from "react";
import { useStallLogger } from "./useStallLogger";
import devLogger from "./devLogger";

// `shaka` (the type-only import above) is a pure namespace — fine for dotted type refs like
// `shaka.Player`/`shaka.util.Error`, but a bare namespace can't itself be used as a type (it
// has no "namespace object type" here). `ShakaModule` is that: the shape of the whole loaded
// library, for the one place we need to hold "the dynamically-imported module" as a value type.
type ShakaModule = typeof shaka;

interface ShakaPlayerProps {
  src: string;
  drm?: DrmConfig;
  isSoundEnabled?: boolean;
  startTime?: number;
  onPlaybackComplete: () => void;
  onError: (error: Error, level: PlaybackErrorLevel) => void;
  onEvent?: (event: string) => void;
  /** ADDITIVE: fires once a key system is negotiated (or explicitly null / none offered). */
  onDrmNegotiated?: (keySystem: string | null) => void;
  /** ADDITIVE: fires on a Shaka error whose `category === 6` (DRM) — Foyer's glue layer uses
   * this to trigger the native ExoPlayer fallback via FoyerBridge.startNativePlayback(). */
  onDrmCategory6Failure?: () => void;
  /**
   * ADDITIVE, default `true` (matches every existing call site exactly — this
   * prop changes nothing for the single-layer case). `false` primes the
   * manifest (attach/configure/load, same DRM-lifecycle discipline as always)
   * WITHOUT calling `.play()` — for Foyer's pre-roll layer pair (the port
   * plan's §6b), where an "upcoming" instance must have its decoder warm
   * before its turn without competing for audio/screen with the active one.
   * Flipping this prop later (on the SAME mounted instance — the caller must
   * not change `key` when only `active` changes, or the whole point of
   * priming is lost) calls `.play()`/`.pause()` without touching `src` or
   * re-attaching — the same "hand over, don't rebuild" principle
   * `SignageSurface.tsx`'s own `MediaLayer` already established, applied to
   * this per-layer instance instead of a shared DOM element.
   *
   * While inactive, the startup watchdog's success signal is "the manifest
   * loaded", not "playback started" — `.play()` is never called, so
   * "playing" never fires, and waiting for it would always time out. This is
   * NOT a weaker guarantee for the default/active case: that path is
   * unchanged, still gated on the real `playing` event, exactly as it always
   * was — see the file's own top-of-file note on why this component exists.
   */
  active?: boolean;
  /**
   * ADDITIVE (2026-09-28 cold-start stall): fires once, on this instance's
   * first real `playing` event - "the decoder is actually producing frames",
   * not "play() was called". Foyer's `DualLayerPlayback` waits for it before
   * mounting a pre-roll layer, so a cold mount never runs two fresh Shaka
   * pipelines at once. Unset → no behavior change.
   */
  onPlaybackStarted?: () => void;
}

// A DRM stream whose keys never become usable loads cleanly and then sits on a
// black screen forever: useStallLogger only arms itself after the first
// `playing`, so nothing downstream notices. Give up and let the caller fail
// over instead of parking the screen.
const SHAKA_STARTUP_FAIL_MS = 30000;

// A play() interrupted by a pause() nobody in THIS component asked for (see
// the play/pause effect below) is retried this many times, this far apart,
// before the startup watchdog is left to fail the item as before.
const PLAY_INTERRUPTED_RETRY_MS = 1000;
const PLAY_INTERRUPTED_MAX_RETRIES = 3;

// A deployed WGT/APK has no reachable console; Foyer's own in-app log ring buffer
// (src/tv/lib/logBuffer.ts) is the visible sink there — fall back to it if present,
// otherwise plain console.log, mirroring the upstream `dbg()` pattern.
// How many Shaka players exist RIGHT NOW (created, not yet fully destroyed).
// `player.destroy()` is async and is not awaited on unmount, so at an item
// change the outgoing player can still be tearing down (DRM sessions, decoder)
// while the incoming one loads. Logged on create and destroy so the overlap
// is visible in the shipped log: 2026-09-28 22:00Z a Samsung QB24C froze on
// the switch from a Widevine live channel to a clear playlist item, with the
// old session still requesting licences after the new load had begun.
let alivePlayers = 0;

const dbg = (msg: string) => {
  const sink =
    typeof (window as unknown as { dbg?: (m: string) => void }).dbg ===
    "function"
      ? (window as unknown as { dbg: (m: string) => void }).dbg
      : console.log;
  sink(msg);
};

// shaka.util.Error stringifies to "[object Object]", hiding the only part that
// identifies the failure: the numeric code (6007 license request failed,
// 6008 license rejected, 6001 unsupported key system, 6014 no usable key, ...).
const describeShakaError = (error: unknown): string => {
  const detail = error as Partial<shaka.util.Error> | undefined;
  if (!detail || typeof detail !== "object" || detail.code === undefined) {
    return String(error);
  }

  let data: string;
  try {
    data = JSON.stringify(detail.data ?? []).slice(0, 200);
  } catch {
    data = "<unserializable>";
  }

  return `code=${detail.code} category=${detail.category} severity=${detail.severity} data=${data}`;
};

const ShakaPlayer: React.FC<ShakaPlayerProps> = ({
  src,
  drm,
  onPlaybackComplete,
  isSoundEnabled = false,
  startTime,
  onError,
  onEvent,
  onDrmNegotiated,
  onDrmCategory6Failure,
  active = true,
  onPlaybackStarted,
}) => {
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<shaka.Player | null>(null);
  const [Shaka, setShaka] = React.useState<ShakaModule | undefined>(undefined);

  // The load effect outlives the render that created it, so read the callbacks
  // through refs to avoid acting on a stale `resolvedSrc` in the parent.
  const onErrorRef = useRef(onError);
  const onEventRef = useRef(onEvent);
  const onDrmNegotiatedRef = useRef(onDrmNegotiated);
  const onDrmCategory6FailureRef = useRef(onDrmCategory6Failure);
  const onPlaybackStartedRef = useRef(onPlaybackStarted);
  // Set by the load effect's `playing` listener; read by the play/pause
  // effect to know an interrupted play() no longer needs retrying.
  const hasStartedRef = useRef(false);
  // Read as a ref too: `startTime` ticks with playback progress for VOD, and a
  // changing dependency would restart the load on every tick. Same reason for
  // `activeRef`: the load effect's own `.then()` reads "is this still the
  // active layer" at RESOLUTION time, not at the time the load started.
  const startTimeRef = useRef(startTime);
  const activeRef = useRef(active);
  useEffect(() => {
    onErrorRef.current = onError;
    onEventRef.current = onEvent;
    onDrmNegotiatedRef.current = onDrmNegotiated;
    onDrmCategory6FailureRef.current = onDrmCategory6Failure;
    onPlaybackStartedRef.current = onPlaybackStarted;
    startTimeRef.current = startTime;
    activeRef.current = active;
  });

  // Centralizes play()/pause() for every case (initial active mount, a
  // pre-rolled layer promoted to active later, an active layer retired to
  // pre-roll for something else) behind one place instead of scattering
  // `.play()` calls across the load effect and a separate transition effect.
  // Deliberately does NOT touch `src`/attach/configure - flipping `active`
  // alone must never re-trigger any of that, or a primed decoder's whole
  // point is lost.
  //
  // 2026-09-28 hasselbacken/302 cold-start stall: an active layer's play()
  // can be rejected with AbortError "interrupted by a call to pause()" by a
  // pause() this component never made - an always-active layer never
  // reaches the `video.pause()` branch below. On Tizen, Shaka's own stall
  // breaker is `pause(); play()` (lib/device/tizen.js sets stallSkip = 0),
  // and the platform pauses a media element whose decoder another element
  // takes. Before this, nothing ever called play() again: the element sat
  // paused and black until the 30s startup watchdog failed the item. Now an
  // interrupted play() is retried a few times while this layer is still
  // active, still paused and still has not started. Only AbortError is
  // retried: NotAllowedError (autoplay policy) or NotSupportedError will
  // not change their answer a second later.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoLoaded) return;
    if (!active) {
      video.pause();
      return;
    }

    // Set by this effect's cleanup: `active` flipped false (our OWN pause,
    // which rejects the pending play() with the same AbortError) or the
    // component unmounted. Neither should be retried.
    let superseded = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retriesLeft = PLAY_INTERRUPTED_MAX_RETRIES;

    const attemptPlay = () => {
      retryTimer = null;
      const p = video.play();
      if (!p || typeof p.catch !== "function") return;
      p.catch((e: unknown) => {
        onErrorRef.current(
          new Error(
            `Shaka video play() rejected: ${e instanceof Error ? e.message : e}`
          ),
          PlaybackErrorLevel.WARNING
        );
        const interrupted =
          (e as { name?: unknown } | null)?.name === "AbortError";
        if (
          superseded ||
          !interrupted ||
          hasStartedRef.current ||
          !video.paused ||
          retriesLeft <= 0
        ) {
          return;
        }
        retriesLeft -= 1;
        onEventRef.current?.(
          `Shaka retrying interrupted play() in ${PLAY_INTERRUPTED_RETRY_MS}ms (${retriesLeft} retries left after this)`
        );
        retryTimer = setTimeout(attemptPlay, PLAY_INTERRUPTED_RETRY_MS);
      });
    };
    attemptPlay();

    return () => {
      superseded = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [active, isVideoLoaded]);

  const onVideoEnded = () => {
    setIsVideoLoaded(false);
    onPlaybackComplete();
  };

  const handleStallLog = useCallback((msg: string) => {
    if (import.meta.env.DEV) console.log("[StallLogger]", msg);
  }, []);

  const handleLongStall = useCallback(() => {
    onErrorRef.current(
      new Error("Shaka Playback long stall"),
      PlaybackErrorLevel.WARNING
    );
  }, []);

  const handleStallRecovery = useCallback((stallDuration: number) => {
    if (stallDuration > 0) {
      onEventRef.current?.(
        `Shaka playback recovered after ${stallDuration} ms`
      );
    }
  }, []);

  useStallLogger({
    videoRef,
    log: handleStallLog,
    onLongStall: handleLongStall,
    onStallRecovered: handleStallRecovery,
    longStallThresholdMs: 3000,
  });

  // Load Shaka dynamically when we are in the browser. Interop note: shaka-player's build
  // has only a default export; depending on the bundler's ESM/CJS interop this can surface
  // either as the module namespace object directly, or nested under `.default` — exactly the
  // ambiguity Foyer's OWN existing inline Shaka loader (src/tv/signage/SignageSurface.tsx)
  // already handles defensively. Match that proven-in-production pattern here rather than
  // trusting upstream autoplayer's bare `.then((shaka) => setShaka(shaka))`, which was written
  // against Metro's interop behavior, not Vite's.
  useEffect(() => {
    import("shaka-player")
      .then((mod: unknown) => {
        const lib =
          (mod as { default?: ShakaModule }).default ?? (mod as ShakaModule);
        setShaka(lib);
      })
      .catch((e) => {
        console.error("Error loading Shaka Player:", e);
        const message = e instanceof Error ? e.message : String(e);
        const error = new Error(`Failed to load Shaka Player: ${message}`);
        onErrorRef.current(error, PlaybackErrorLevel.FAILURE);
      });
  }, []);

  useEffect(() => {
    if (!Shaka || !videoRef.current) return;

    devLogger.log("Initializing Shaka Player...");
    let player: shaka.Player;
    try {
      Shaka.polyfill.installAll();
      player = new Shaka.Player();
      player.attach(videoRef.current).catch((e: unknown) => {
        onErrorRef.current(
          new Error(`Failed to attach Shaka Player: ${describeShakaError(e)}`),
          PlaybackErrorLevel.FAILURE
        );
      });
      playerRef.current = player;
      alivePlayers += 1;
      dbg(`[shaka] player created (alive=${alivePlayers})`);
    } catch (e) {
      onErrorRef.current(
        new Error(
          `Failed to initialize Shaka Player: ${e instanceof Error ? e.message : e}`
        ),
        PlaybackErrorLevel.FAILURE
      );
      return;
    }

    return () => {
      devLogger.log("Destroying Shaka Player...");
      playerRef.current = null;
      const destroyStart = performance.now();
      dbg(`[shaka] player destroy begin (alive=${alivePlayers})`);
      // Observed, never awaited: awaiting here would change teardown ordering
      // on every screen, and whether it should is exactly what this line exists
      // to find out. `Promise.resolve` because a test double may return void.
      Promise.resolve(player.destroy())
        .catch(() => {})
        .then(() => {
          alivePlayers -= 1;
          dbg(
            `[shaka] player destroyed after ${Math.round(performance.now() - destroyStart)}ms (alive=${alivePlayers})`
          );
        });
    };
  }, [Shaka]);

  useEffect(() => {
    const player = playerRef.current;
    const video = videoRef.current;
    if (!Shaka || !player || !video) return;

    const servers: Record<string, string> = {};
    const advanced: Record<string, { serverCertificateUri: string }> = {};

    if (drm?.widevine?.licenseServerUrl) {
      servers["com.widevine.alpha"] = drm.widevine.licenseServerUrl;
      // Redbee/Verimatrix run Widevine with privacy mode on. Without the
      // service certificate the CDM sends a cleartext client ID and the
      // license server rejects every challenge, so audio and video never
      // decrypt while the unencrypted text stream keeps rendering.
      if (drm.widevine.certificateUrl) {
        advanced["com.widevine.alpha"] = {
          serverCertificateUri: drm.widevine.certificateUrl,
        };
      }
    }

    if (drm?.playready?.licenseServerUrl) {
      servers["com.microsoft.playready"] = drm.playready.licenseServerUrl;
    }

    player.configure({ drm: { servers, advanced } });

    const keySystems = Object.keys(servers);
    const certConfigured = Boolean(drm?.widevine?.certificateUrl);

    const readKeyStatuses = (): string => {
      try {
        return JSON.stringify(player.getKeyStatuses());
      } catch {
        return "<unavailable>";
      }
    };

    // dbg() paints on the panel, which is no help when nobody is standing in
    // front of it. Every DRM diagnostic also goes out through onEvent (which
    // reaches the backend log) so a remote test run can be read afterwards.
    const report = (msg: string) => {
      dbg(`[shaka] ${msg}`);
      onEventRef.current?.(`Shaka ${msg}`);
    };

    // The one line that identifies a DRM failure: what we offered, whether the
    // privacy-mode certificate was applied, which key system the CDM actually
    // picked, and how far the keys got. Appended to the errors that reach
    // observability, because a bare error code doesn't say which CDM produced it.
    const drmSummary = (): string =>
      `offered=${keySystems.join(",") || "none"} cert=${
        certConfigured ? "yes" : "no"
      } selected=${
        player.drmInfo()?.keySystem ?? "none"
      } statuses=${readKeyStatuses()}`;

    if (keySystems.length > 0) {
      report(`drm configured ${drmSummary()}`);
    }
    // ADDITIVE: surface the negotiated key system for real telemetry (SignagePlaybackReport
    // .keySystem), rather than the wall-clock-estimated value the pre-port renderer reported.
    onDrmNegotiatedRef.current?.(player.drmInfo()?.keySystem ?? null);

    let startupTimer: ReturnType<typeof setTimeout> | null = null;
    const clearStartupWatchdog = () => {
      if (startupTimer !== null) {
        clearTimeout(startupTimer);
        startupTimer = null;
      }
    };

    // Only reveal subtitles once pictures are actually flowing. Otherwise a
    // stream that loads but never decrypts renders as subtitles on black,
    // which reads as "it plays only subtitles" rather than as a failure.
    //
    // KNOWN GAP, deliberately deferred: in Shaka v5 (what Foyer ships, ^5.2.4 — autoplayer's
    // own package.json pins ^4.13.6) `setTextTrackVisibility`/`setTextVisibility` moved OFF
    // the `Player` class entirely onto `shaka.extern.TextDisplayer`, with no accessor exposed
    // on `Player` in this ("compiled", non-UI) build to reach it. Selecting a track still
    // works (`selectTextTrack`); only the explicit visibility toggle doesn't have a v5
    // equivalent reachable from here without pulling in Shaka's UI extension bundle, which
    // this port doesn't otherwise need. This is cosmetic (subtitle auto-show) and orthogonal
    // to the DRM/decoder-lifecycle bug this port exists to fix — call it defensively so a
    // future Shaka build that DOES expose the method still gets it, and revisit if signage
    // content with burned-in captions actually needs this later.
    const showTextTrack = () => {
      const textTracks = player.getTextTracks();
      if (textTracks.length > 0) {
        player.selectTextTrack(textTracks[0]);
        const maybeSetTextVisibility = (
          player as unknown as { setTextVisibility?: (on: boolean) => unknown }
        ).setTextVisibility;
        maybeSetTextVisibility?.(true);
      }
    };

    let hasStarted = false;
    hasStartedRef.current = false;
    const onPlaying = () => {
      if (hasStarted) return;
      hasStarted = true;
      hasStartedRef.current = true;
      clearStartupWatchdog();
      report(`playing ${drmSummary()}`);
      showTextTrack();
      onPlaybackStartedRef.current?.();
    };
    video.addEventListener("playing", onPlaying);

    const onKeyStatusChanged = () => {
      report(`key statuses ${readKeyStatuses()}`);
    };
    player.addEventListener("keystatuschanged", onKeyStatusChanged);

    const onShakaError = (event: Event) => {
      const detail = (event as CustomEvent<shaka.util.Error>).detail;
      const description = describeShakaError(detail);
      console.error("Shaka Player error:", detail);
      report(`error ${description}`);
      // ADDITIVE: category 6 = DRM. Let Foyer's glue layer decide whether to fail over to
      // the native ExoPlayer path (FoyerBridge.startNativePlayback) rather than baking that
      // platform decision into this component.
      if (detail?.category === 6) {
        onDrmCategory6FailureRef.current?.();
      }
      onErrorRef.current(
        new Error(`Shaka Player error: ${description} (${drmSummary()})`),
        PlaybackErrorLevel.WARNING
      );
    };
    player.addEventListener("error", onShakaError);

    const LICENSE = Shaka.net.NetworkingEngine.RequestType.LICENSE;
    const netEngine = player.getNetworkingEngine();
    const logLicenseRequest = (
      type: shaka.net.NetworkingEngine.RequestType,
      request: shaka.extern.Request
    ) => {
      if (type === LICENSE) {
        report(`license request ${request.uris[0]?.slice(0, 80) ?? "?"}`);
      }
    };
    const logLicenseResponse = (
      type: shaka.net.NetworkingEngine.RequestType,
      response: shaka.extern.Response
    ) => {
      if (type === LICENSE) {
        report(`license response ${response.data?.byteLength ?? 0}B`);
      }
    };
    netEngine?.registerRequestFilter(logLicenseRequest);
    netEngine?.registerResponseFilter(logLicenseResponse);

    /*
     * 2026-09-22/23 THROUGH 2026-09-24 incidents (hasselbacken/302): two gaps
     * this file had zero visibility into, found only by reading the actual
     * code after a live 18-hour-then-black stall with no error, no circuit-
     * breaker trip, and nothing in the log ring buffer but routine platform
     * housekeeping:
     *
     * 1. `useStallLogger` (below, unchanged - it is a verbatim port and stays
     *    one) arms its timer ONLY on the DOM `waiting` event. `waiting` fires
     *    when the element runs out of BUFFERED data - it says nothing about
     *    whether buffered data is actually being DECODED. This hardware's
     *    own proven failure mode (High-profile H.264, measured repeatedly
     *    this same week) is exactly the opposite shape: MediaSource has the
     *    bytes, `readyState` can sit at HAVE_ENOUGH_DATA indefinitely, and
     *    the decoder simply never produces a frame - `waiting` never fires,
     *    `useStallLogger` never arms, `handleLongStall` never runs. This is
     *    not a severity bug (WARNING vs FAILURE); the detector is watching
     *    the wrong event for this failure class and stays permanently silent
     *    for it.
     * 2. Nothing recorded WHICH rendition Shaka's own ABR had selected. An
     *    18-hour clean run through this same content before one single
     *    failure rules out "always picks the bad one" (that would fail
     *    immediately and every time, not once after dozens of clean passes)
     *    - but with no `adaptation` visibility, there was no way to tell
     *    whether the estimated bandwidth had drifted into 1080p/High-profile
     *    territory for the first time on this particular attempt, or
     *    whether this is unrelated to codec/profile at all.
     *
     * Deliberately OBSERVABILITY ONLY - neither addition changes playback,
     * retry, or recovery behavior. The goal is a real ring-buffer trail the
     * NEXT time this happens, not a guess about what happened THIS time.
     * Both go through `report()` (dbg() + onEvent, the same double path
     * every other diagnostic in this file already uses) rather than
     * `devLogger`, which is compiled to a no-op in production and would have
     * been just as silent as the gap this is closing.
     */
    const describeActiveTrack = (): string => {
      try {
        const active = player
          .getVariantTracks()
          .find((t) => t.active && t.type === "variant");
        if (!active) return "none";
        return `id=${active.id} ${active.width}x${active.height} codecs=${active.videoCodec ?? "?"} bw=${active.bandwidth}`;
      } catch {
        return "<unavailable>";
      }
    };
    const onAdaptation = () => {
      report(`adaptation -> ${describeActiveTrack()}`);
    };
    player.addEventListener("adaptation", onAdaptation);

    // `waiting`/`playing`-independent stall detection: polls actual decode
    // PROGRESS (`video.currentTime`) directly, the same signal
    // `SignageSurface.tsx`'s own pre-port watchdog already trusted for
    // exactly this reason. Silent while healthy (an unconditional heartbeat
    // would just get evicted by this device's own housekeeping noise inside
    // the 500-line ring buffer - see logBuffer.ts - well before a rare,
    // hours-apart stall ever reached a capture); loud and REPEATING the
    // moment two consecutive checks show no advancement, so whichever
    // capture happens to land after someone notices the wall stays black,
    // the ring buffer's tail is dominated by exactly this, not routine
    // platform polling.
    const PROGRESS_CHECK_MS = 15_000;
    let lastCurrentTime = video.currentTime;
    let frozenSinceMs: number | null = null;
    let pausedTicks = 0;
    // A confirmed stall is ALSO published to the heartbeat
    // (`SignagePlaybackReport.media`), because a log line is read by whoever
    // goes looking and the fleet alert is what tells someone to look at all.
    let stallPublished = false;
    const publishStall = (summary: string) => {
      stallPublished = true;
      reportSignageMediaStall(summary.slice(0, 400));
    };
    const clearPublishedStall = () => {
      if (!stallPublished) return;
      stallPublished = false;
      reportSignageMediaStall(null);
    };
    const progressTimer = setInterval(() => {
      if (!activeRef.current) return;
      if (video.paused) {
        // An ACTIVE layer that already started and is now paused: nothing
        // here asked for that (`active` drives play/pause in one place) and
        // it never resumes on its own. This used to `return` silently, so a
        // video parked on its first frame by an interrupted play() was
        // invisible to this watchdog. A layer that has NOT yet started is the
        // startup watchdog's job, not this one's.
        if (!hasStartedRef.current) return;
        pausedTicks += 1;
        if (pausedTicks >= 2) {
          const summary = `paused while active ${pausedTicks * (PROGRESS_CHECK_MS / 1000)}s t=${video.currentTime.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState} ended=${video.ended} track=${describeActiveTrack()}`;
          report(summary);
          publishStall(summary);
        }
        return;
      }
      pausedTicks = 0;
      const now = video.currentTime;
      const advanced = now - lastCurrentTime;
      lastCurrentTime = now;
      if (advanced > 0.5) {
        if (frozenSinceMs !== null) {
          report(
            `progress recovered after ${Math.round((performance.now() - frozenSinceMs) / 1000)}s -> ${describeActiveTrack()}`
          );
        }
        frozenSinceMs = null;
        clearPublishedStall();
        return;
      }
      if (frozenSinceMs === null) {
        // First quiet tick - could be a legitimate brief pause between
        // segments. Only escalate from the SECOND consecutive miss.
        frozenSinceMs = performance.now();
        return;
      }
      // `decodedFrames` frozen alongside `currentTime` while `droppedFrames`
      // and `bandwidth`/`buffered` look healthy is the direct signature of a
      // decoder that has the data and simply refuses to produce pictures
      // (this hardware's own proven High-profile behavior) - as opposed to
      // `decodedFrames` still climbing, which would point somewhere else
      // entirely (a compositor/render-surface problem, not a decode one).
      let bandwidth = "?";
      let frameStats = "?";
      try {
        const stats = player.getStats();
        bandwidth = String(stats.estimatedBandwidth);
        frameStats = `decoded=${stats.decodedFrames} dropped=${stats.droppedFrames} stallsDetected=${stats.stallsDetected}`;
      } catch {
        /* stats unavailable this early/late in the lifecycle - not fatal */
      }
      const buffered: string[] = [];
      try {
        for (let i = 0; i < video.buffered.length; i += 1) {
          buffered.push(
            `${video.buffered.start(i).toFixed(1)}-${video.buffered.end(i).toFixed(1)}`
          );
        }
      } catch {
        /* TimeRanges access can throw on a torn-down element */
      }
      const stalledSummary = `progress stalled ${Math.round((performance.now() - frozenSinceMs) / 1000)}s t=${now.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState} buffered=[${buffered.join(",")}] bandwidth=${bandwidth} ${frameStats} track=${describeActiveTrack()}`;
      report(stalledSummary);
      publishStall(stalledSummary);
    }, PROGRESS_CHECK_MS);

    startupTimer = setTimeout(() => {
      startupTimer = null;
      // An INACTIVE layer never calls `.play()`, so "playing" never fires -
      // waiting for it would always time out and fail a pre-roll layer that
      // primed correctly. `active` (the current prop, not what it was when
      // the load started - a layer can be promoted mid-load) decides which
      // signal this timer is actually watching for.
      if (!activeRef.current) return;
      const summary = drmSummary();
      report(`no first frame after ${SHAKA_STARTUP_FAIL_MS}ms ${summary}`);
      onErrorRef.current(
        new Error(
          `Shaka never started playback within ${SHAKA_STARTUP_FAIL_MS}ms (${summary})`
        ),
        PlaybackErrorLevel.FAILURE
      );
    }, SHAKA_STARTUP_FAIL_MS);

    player
      .load(src, startTimeRef.current)
      .then(() => {
        setIsVideoLoaded(true);
        // Manifest primed. `.play()` itself is centralized in the
        // `[active, isVideoLoaded]` effect above - an inactive layer stops
        // here, decoder warm, nothing playing, watchdog already accounted
        // for above. An active layer's watchdog stays armed until the real
        // `playing` event clears it (unchanged from before this prop
        // existed).
        if (!activeRef.current) clearStartupWatchdog();
      })
      .catch((error) => {
        // A load aborted by unmount or by a new src is not a playback failure.
        const code = (error as shaka.util.Error | undefined)?.code;
        if (
          code === Shaka.util.Error.Code.LOAD_INTERRUPTED ||
          code === Shaka.util.Error.Code.OPERATION_ABORTED
        ) {
          return;
        }
        clearStartupWatchdog();
        const description = describeShakaError(error);
        console.error("Error loading video:", error);
        report(`load failed ${description}`);
        onErrorRef.current(
          new Error(`Shaka load failed: ${description} (${drmSummary()})`),
          PlaybackErrorLevel.FAILURE
        );
      });

    return () => {
      clearStartupWatchdog();
      clearInterval(progressTimer);
      // This player is going away (new item, or unmount): whatever it was
      // stuck on is no longer on screen.
      clearPublishedStall();
      video.removeEventListener("playing", onPlaying);
      player.removeEventListener("error", onShakaError);
      player.removeEventListener("keystatuschanged", onKeyStatusChanged);
      player.removeEventListener("adaptation", onAdaptation);
      netEngine?.unregisterRequestFilter(logLicenseRequest);
      netEngine?.unregisterResponseFilter(logLicenseResponse);
    };
  }, [src, drm, Shaka]);

  const handleError = (
    event: React.SyntheticEvent<HTMLVideoElement, Event>
  ) => {
    const error = event.currentTarget.error;
    if (error) {
      const errorMessage = `Video playback error: code=${error.code} ${error.message}`;
      console.error(errorMessage);
      dbg(`[shaka] ${errorMessage}`);
      onErrorRef.current(new Error(errorMessage), PlaybackErrorLevel.FAILURE);

      setIsVideoLoaded(false);
    }
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <video
        onEnded={onVideoEnded}
        onError={handleError}
        ref={videoRef}
        className="shaka-video"
        controls={false}
        style={{
          width: "100%",
          height: "100%",
          display: isVideoLoaded ? "block" : "none",
        }}
        playsInline={true}
        autoPlay={active}
        muted={!isSoundEnabled}
      />
    </div>
  );
};

export default memo(ShakaPlayer);
